import { memo, useCallback, useMemo } from 'react';
import { formControlClasses, inputClasses, selectClasses } from '@mui/material';
import { Stack } from '@mui/system';
import { ManualArea, RackingArea, RackLoadTypeSpec, SingularStationArea } from '@thive/fddm-types';
import { useShallow } from 'zustand/react/shallow';

import { SelectInput } from '@/modules/common/components/inputs';
import { minItemsArrayUtils } from '@/modules/floorplanDesign/helpers/minItems';
import { useLoadCarrierStore } from '@/modules/loadCarriers/store/useLoadCarrierStore';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { PropertiesLabel } from '@/modules/common/components/PropertiesLabel';
import { assertNever } from '@/helpers/utils';
import { getCommonOption } from '@/modules/common/helpers/commonValue';
import {
  createEmptyAreas,
  pushAreaToItsCollection,
} from '@/modules/floorplanDesign/helpers/helpers';
import { logger } from '@common/logging/logger';

/** The area types that carry a load type. */
type LoadTypeArea = SingularStationArea | RackingArea | ManualArea;

interface Props {
  selectedAreas: LoadTypeArea[];
}

type SelectedLoadType = { id: string; width: number; length: number };

const containerSx = {
  // Wrap value-string to new line, don't truncate.
  [`& .${selectClasses.multiple}`]: {
    whiteSpace: 'normal!important',
  },

  // because value-string is wrapped, correct the height and padding of the options container.
  [`& .${formControlClasses.root} .${inputClasses.root}`]: {
    height: 'auto',
    padding: '4px 12px!important',
  },
};

// TODO: support multiple load types per area; for now the first one is used
const getLoadTypeId = (area: LoadTypeArea): string => {
  switch (area.areaType) {
    case 'SINGULAR':
    case 'MANUAL':
      return area.stationSpec.loadTypeIds[0];
    case 'RACKING':
      return area.rackSpec.loadTypes[0].loadTypeId;
    default:
      return assertNever(area);
  }
};

/**
 * @description Returns the area with the given load type. Singular and manual areas also take the
 * load type's size as their station size.
 */
const withLoadType = (area: LoadTypeArea, loadType: SelectedLoadType): LoadTypeArea => {
  switch (area.areaType) {
    case 'SINGULAR':
    case 'MANUAL':
      return {
        ...area,
        stationSpec: {
          ...area.stationSpec,
          loadTypeIds: [loadType.id],
          width: loadType.width,
          length: loadType.length,
        },
      };
    case 'RACKING': {
      const loadTypes = minItemsArrayUtils.mapAtLeast1<string, RackLoadTypeSpec>(
        [loadType.id],
        (value, index) => ({
          loadTypeId: value,
          stationRegex: area.rackSpec.loadTypes[index]?.stationRegex || undefined,
        }),
      );

      return {
        ...area,
        rackSpec: {
          ...area.rackSpec,
          loadTypes,
        },
      };
    }
    default:
      return assertNever(area);
  }
};

function LoadTypeComponent({ selectedAreas }: Props) {
  const allLoadCarriers = useLoadCarrierStore((state) => state.allLoadCarriers);
  const { loadTypesInStore, getLoadTypeById } = useFloorplanDesignStore(
    useShallow((state) => ({
      loadTypesInStore: state.setup.loadTypes,
      getLoadTypeById: state.getLoadTypeById,
    })),
  );
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);

  const enabledLoadTypeIds = useMemo(
    () => loadTypesInStore.map(({ id }) => id),
    [loadTypesInStore],
  );
  const options = useMemo(
    () =>
      allLoadCarriers.getByIds(enabledLoadTypeIds).map(({ name, id }) => ({
        label: name,
        value: id,
      })),
    [allLoadCarriers, enabledLoadTypeIds],
  );

  const loadType = useMemo(
    () => getCommonOption(selectedAreas.map(getLoadTypeId)),
    [selectedAreas],
  );

  const onChange = useCallback(
    (newValue: string) => {
      const selectedLoadType = getLoadTypeById(newValue);
      if (!selectedLoadType) {
        logger.error(`LoadType: load type with id ${newValue} not found.`);
        return;
      }

      const updatedAreas = createEmptyAreas();
      selectedAreas.forEach((area) => {
        pushAreaToItsCollection(updatedAreas, withLoadType(area, selectedLoadType));
      });
      fddmSvc.updateAreas(updatedAreas);
    },
    [fddmSvc, getLoadTypeById, selectedAreas],
  );

  return (
    <PropertiesLabel i18nkey='interface:properties.area.load_carrier_select.label'>
      <Stack sx={containerSx}>
        <SelectInput options={options} onChange={onChange} value={loadType} showMixed />
      </Stack>
    </PropertiesLabel>
  );
}

export const LoadType = memo(LoadTypeComponent);
